import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { ChevronLeft, MessagesSquare, Calendar, FileText, CheckCircle2, Circle, Loader2, AlertCircle } from "lucide-react";

export default function CollabProjectDetail() {
  const { type, id } = useParams();
  const kind = type === "circle" ? "circle" : "offering";
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageMeta(data?.title || t("client_collab_projects_title"));

  const load = useCallback(async () => {
    try { const { data: r } = await api.get(`/client/collab/projects/${kind}/${id}/detail`); setData(r); }
    catch { setData(null); } finally { setLoading(false); }
  }, [kind, id]);
  useEffect(() => { load(); }, [load]);

  const openChat = () => {
    if (!data) return;
    if (kind === "circle" && data.circle_id) navigate(`/app/circles?c=${data.circle_id}`);
    else if (data.shared_chat_id) navigate(`/app/shared-chat/${data.shared_chat_id}`);
  };
  const canChat = !!(data?.shared_chat_id || (kind === "circle" && data?.circle_id));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (!data) {
    return (
      <div className="mx-auto max-w-2xl">
        <button onClick={() => navigate("/app/collab/projects")} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div className="flex flex-col items-center gap-2 py-16 text-center"><AlertCircle className="h-8 w-8 text-muted-foreground/60" /><p className="text-sm text-muted-foreground">{t("client_collab_project_not_found")}</p></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="client-collab-project-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/collab/projects")} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div className="min-w-0">
          <h1 className="truncate font-display text-2xl text-foreground">{data.title}</h1>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{kind === "circle" ? t("client_collab_kind_circle") : t("client_collab_kind_offering")}</p>
        </div>
      </div>

      {(data.coaches || []).length > 0 ? (
        <Section title={t("client_collab_project_coaches")}>
          <div className="flex flex-wrap gap-2">
            {data.coaches.map((c) => (
              <button key={c.id} data-testid={`project-coach-${c.id}`} onClick={() => navigate(`/coaches/${c.id}`)} className="flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5">
                <span className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-primary/20 text-[11px] font-semibold text-primary">{c.photo ? <img src={c.photo} alt="" className="h-full w-full object-cover" /> : (c.name || "?")[0]?.toUpperCase()}</span>
                <span className="text-sm text-foreground">{c.name}</span>
              </button>
            ))}
          </div>
        </Section>
      ) : null}

      {data.description ? <Section title={t("client_collab_project_about")}><p className="text-sm leading-relaxed text-muted-foreground">{data.description}</p></Section> : null}

      <Section title={t("client_collab_project_chat")}>
        {canChat ? (
          <button data-testid="project-open-chat" onClick={openChat} className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground"><MessagesSquare className="h-4 w-4" /> {t("client_collab_project_open_chat")}</button>
        ) : <p className="text-sm italic text-muted-foreground">{t("client_collab_project_chat_soon")}</p>}
      </Section>

      <Section title={t("client_collab_project_sessions")}>
        {(data.sessions || []).length === 0 ? <p className="text-sm italic text-muted-foreground">{t("client_collab_project_sessions_empty")}</p> : data.sessions.map((sess, i) => (
          <Row key={i} icon={Calendar} color="#3E7C6A" title={sess.title || t("session")} meta={sess.period === "week" ? `${sess.count || 1}× / ${t("week")}` : sess.period === "month" ? `${sess.count || 1}× / ${t("month")}` : sess.period === "total" ? `${sess.count || 1}× ${t("total")}` : sess.date_iso || ""} />
        ))}
      </Section>

      <Section title={t("client_collab_project_documents")}>
        {(data.documents || []).length === 0 ? <p className="text-sm italic text-muted-foreground">{t("client_collab_project_documents_empty")}</p> : data.documents.map((doc, i) => (
          <Row key={i} icon={FileText} color="#2E7BA6" title={doc.title || doc.name || t("document")} meta={doc.description} />
        ))}
      </Section>

      <Section title={t("client_collab_project_tasks")}>
        {(data.tasks || []).length === 0 ? <p className="text-sm italic text-muted-foreground">{t("client_collab_project_tasks_empty")}</p> : data.tasks.map((tk, i) => (
          <div key={i} className="flex items-center gap-2.5 py-1.5">
            {tk.done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="h-4 w-4 shrink-0 text-amber-500" />}
            <span className={`text-sm ${tk.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{tk.title}</span>
          </div>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2 rounded-2xl border border-border bg-secondary/60 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground">{title}</p>
      {children}
    </div>
  );
}
function Row({ icon: Icon, color, title, meta }) {
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: color + "22" }}><Icon className="h-3.5 w-3.5" style={{ color }} /></span>
      <span className="min-w-0 flex-1"><span className="block truncate text-sm text-foreground">{title}</span>{meta ? <span className="block truncate text-xs text-muted-foreground">{meta}</span> : null}</span>
    </div>
  );
}
